import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, User, Chrome, Briefcase, ShoppingBag, Gift } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('customer');
  const [referralCode, setReferralCode] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setReferralCode(ref.toUpperCase());
      toast.success(`Referral code ${ref.toUpperCase()} applied - R50 welcome credit!`);
    }
  }, [searchParams]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name, email, password, role,
          referral_code: referralCode.trim() || null
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Registration failed');
      }

      const user = await response.json();
      toast.success('Account created successfully!');
      
      if (user.role === 'customer') {
        navigate('/customer', { state: { user } });
      } else {
        navigate('/runner', { state: { user } });
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRegister = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/customer';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-white flex items-center justify-center px-4">
      <Card data-testid="register-card" className="w-full max-w-md bg-white border border-slate-100 shadow-lg rounded-xl p-8">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="Errands Boy" className="h-24 w-24 object-contain mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Join Errands Boy</h1>
          <p className="text-slate-600">Create your account and get started</p>
        </div>

        <form onSubmit={handleRegister} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Full Name</label>
            <div className="relative">
              <User className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
              <Input
                data-testid="name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="pl-10 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-teal-700/20 focus:border-teal-700 rounded-lg input-focus"
                placeholder="John Doe"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
              <Input
                data-testid="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-teal-700/20 focus:border-teal-700 rounded-lg input-focus"
                placeholder="you@example.com"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
              <Input
                data-testid="password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-teal-700/20 focus:border-teal-700 rounded-lg input-focus"
                placeholder="••••••••"
                required
                minLength={6}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-3">I want to</label>
            <div className="grid grid-cols-2 gap-4">
              <button
                data-testid="role-customer-btn"
                type="button"
                onClick={() => setRole('customer')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  role === 'customer'
                    ? 'border-teal-700 bg-teal-50'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
              >
                <ShoppingBag className={`w-6 h-6 mx-auto mb-2 ${
                  role === 'customer' ? 'text-teal-700' : 'text-slate-400'
                }`} />
                <p className={`text-sm font-medium ${
                  role === 'customer' ? 'text-teal-700' : 'text-slate-600'
                }`}>Book Errands</p>
              </button>
              <button
                data-testid="role-runner-btn"
                type="button"
                onClick={() => setRole('runner')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  role === 'runner'
                    ? 'border-teal-700 bg-teal-50'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
              >
                <Briefcase className={`w-6 h-6 mx-auto mb-2 ${
                  role === 'runner' ? 'text-teal-700' : 'text-slate-400'
                }`} />
                <p className={`text-sm font-medium ${
                  role === 'runner' ? 'text-teal-700' : 'text-slate-600'
                }`}>Run Errands</p>
              </button>
            </div>
          </div>

          <Button
            data-testid="register-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full bg-teal-700 hover:bg-teal-800 text-white rounded-full py-3 font-medium shadow-lg hover:shadow-teal-700/25 btn-hover"
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </Button>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-1.5">
              <Gift className="w-4 h-4 text-yellow-500" />
              Referral Code (optional)
            </label>
            <Input
              data-testid="referral-code-input"
              type="text"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              className="bg-slate-50 border-slate-200 focus:ring-2 focus:ring-yellow-500/20 focus:border-yellow-500 rounded-lg input-focus uppercase tracking-widest text-center font-medium"
              placeholder="ENTER CODE"
              maxLength={12}
            />
            {referralCode && (
              <p className="text-xs text-yellow-600 mt-1 flex items-center gap-1">
                <Gift className="w-3 h-3" /> R50 welcome credit will be applied
              </p>
            )}
          </div>
        </form>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-200"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-slate-500">Or continue with</span>
          </div>
        </div>

        <Button
          data-testid="google-register-btn"
          onClick={handleGoogleRegister}
          className="w-full bg-white hover:bg-slate-50 text-slate-900 border border-slate-200 rounded-full py-3 font-medium btn-hover flex items-center justify-center gap-2"
        >
          <Chrome className="w-5 h-5" />
          Continue with Google
        </Button>

        <p className="text-center text-sm text-slate-600 mt-6">
          Already have an account?{' '}
          <button
            data-testid="login-link"
            onClick={() => navigate('/login')}
            className="text-teal-700 font-medium hover:underline"
          >
            Login
          </button>
        </p>
      </Card>
    </div>
  );
}